import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import apiService from '../services/api';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (profileData: Partial<UserProfile>) => Promise<void>;
  loading: boolean;
  authInitialized: boolean;
}

interface UserProfile {
  uid: string;
  email: string;
  name: string;
  active: boolean;
  loginEnabled: boolean;
  roles: string[];
  locationIds: string[];
  createdAt: Date;
  updatedAt: Date;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    await loadUserProfile(userCredential.user.uid);
  };



  const logout = async () => {
    await signOut(auth);
    setUserProfile(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const loadUserProfile = useCallback(async (uid: string) => {
    try {
      const userData = await apiService.getUserMe();
      if (userData) {
        const profileData: UserProfile = {
          uid: userData.id,
          email: userData.email,
          name: userData.name,
          active: userData.active === 'yes',
          loginEnabled: userData.loginEnabled === 'yes',
          roles: userData.roles?.map((role: any) => role.name) || [],
          locationIds: userData.locations?.map((location: any) => location.location_id.toString()) || [],
          createdAt: new Date(userData.createdAt),
          updatedAt: new Date(userData.createdAt), // Use createdAt as updatedAt for now
        };
        setUserProfile(profileData);
      } else {
        console.error('User profile not found');
        // If user profile not found, the Firebase Auth trigger should have created it automatically
        // Wait a moment and retry, as the trigger might still be processing
        if (currentUser) {
          console.log('User profile not found, waiting for Firebase Auth trigger to create it...');
          setTimeout(async () => {
            try {
              const retryUserData = await apiService.getUserMe();
              if (retryUserData) {
                const profileData: UserProfile = {
                  uid: retryUserData.id,
                  email: retryUserData.email,
                  name: retryUserData.name,
                  active: retryUserData.active === 'yes',
                  loginEnabled: retryUserData.loginEnabled === 'yes',
                  roles: retryUserData.roles?.map((role: any) => role.name) || [],
                  locationIds: retryUserData.locations?.map((location: any) => location.location_id.toString()) || [],
                  createdAt: new Date(retryUserData.createdAt),
                  updatedAt: new Date(retryUserData.createdAt),
                };
                setUserProfile(profileData);
              } else {
                console.error('User profile still not found after waiting for trigger');
              }
            } catch (retryError) {
              console.error('Error retrying user profile load:', retryError);
            }
          }, 3000); // Wait 3 seconds for the trigger to complete
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }, [currentUser]);

  const updateUserProfile = async (profileData: Partial<UserProfile>) => {
    if (!currentUser) return;

    try {
      const updateData: any = {};
      if (profileData.name) updateData.name = profileData.name;
      if (profileData.email) updateData.email = profileData.email;

      await apiService.updateUser(currentUser.uid, updateData);
      
      if (userProfile) {
        setUserProfile({ 
          ...userProfile, 
          ...profileData,
          updatedAt: new Date(),
        });
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    const initializeAuth = async () => {
      try {
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
            setCurrentUser(user);
            await loadUserProfile(user.uid);
          } else {
            setCurrentUser(null);
            setUserProfile(null);
          }
          setLoading(false);
          setAuthInitialized(true);
        });
      } catch (error) {
        console.error('Error setting up auth state listener:', error);
        setLoading(false);
        setAuthInitialized(true);
      }
    };

    initializeAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [loadUserProfile]);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    loading,
    authInitialized,
  };

  return (
    <AuthContext.Provider value={value}>
      {authInitialized && !loading && children}
    </AuthContext.Provider>
  );
}; 